<template>
  <header class="w-full sticky top-0 z-[100]">
    <!-- Backdrop with glass effect -->
    <div
      class="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200/50 dark:border-gray-700/50"
    />

    <nav class="container mx-auto px-4 relative">
      <div class="flex items-center h-20">
        <!-- Left Section: Logo and Navigation -->
        <div class="flex items-center flex-1 gap-8">
          <!-- Logo -->
          <nuxt-link to="/" class="flex-shrink-0 flex items-center group">
            <img
              src="../../assets/images/logo-black.svg"
              class="h-10 w-auto transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"
              alt="Academic Alerts"
            />
          </nuxt-link>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center gap-2">
            <HeaderNavItem
              v-for="(link, index) in navigationConfig.mainNav"
              :key="index"
              v-bind="link"
              class="nav-item"
            />
          </div>
        </div>

        <!-- Right Section: Auth -->
        <div class="hidden md:flex items-center gap-3">
          <template v-if="!user">
            <nuxt-link
              to="/auth/login"
              class="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-primary dark:text-gray-200 transition-all hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              Sign In
            </nuxt-link>
            <nuxt-link
              to="/auth/signup"
              class="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              Sign Up
            </nuxt-link>
          </template>

          <!-- User Menu -->
          <div v-else class="relative user-menu">
            <button
              @click="isUserMenuOpen = !isUserMenuOpen"
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all group"
            >
              <div
                class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"
              >
                <span class="text-sm font-medium text-primary">
                  {{ user.email[0].toUpperCase() }}
                </span>
              </div>
              <span
                class="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-primary transition-colors"
              >
                {{ user.email }}
              </span>
              <Icon
                :name="
                  isUserMenuOpen
                    ? 'heroicons:chevron-up'
                    : 'heroicons:chevron-down'
                "
                class="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors"
              />
            </button>

            <!-- Dropdown Menu -->
            <div
              v-if="isUserMenuOpen"
              class="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
            >
              <div class="py-1.5">
                <nuxt-link
                  to="/dashboard"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <Icon
                    name="heroicons:squares-2x2"
                    class="w-4 h-4 text-gray-400"
                  />
                  Dashboard
                </nuxt-link>
                <button
                  @click="handleLogout"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  <Icon
                    name="heroicons:arrow-right-on-rectangle"
                    class="w-4 h-4"
                  />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Toggle -->
        <button
          class="md:hidden p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isMenuOpen = !isMenuOpen"
          aria-label="Toggle menu"
        >
          <Icon
            :name="isMenuOpen ? 'heroicons:x-mark' : 'heroicons:bars-3'"
            class="w-6 h-6 text-gray-600 dark:text-gray-300"
          />
        </button>
      </div>
    </nav>

    <!-- Mobile Navigation -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <HeaderMobileNav
          v-if="isMenuOpen"
          :isOpen="isMenuOpen"
          @close="isMenuOpen = false"
          :user="user"
        />
      </Transition>
    </Teleport>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { navigationConfig } from "~/config/navigation";

// State
const isMenuOpen = ref(false);
const isUserMenuOpen = ref(false);

// Composables
const route = useRoute();
const router = useRouter();
const user = useStrapiUser();
const { logout } = useStrapiAuth();

// Methods
const handleLogout = async () => {
  await logout();
  router.push("/");
  isUserMenuOpen.value = false;
};

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (!target.closest(".user-menu")) {
    isUserMenuOpen.value = false;
  }
};

const handleResize = () => {
  if (window.innerWidth >= 768) {
    isMenuOpen.value = false;
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    isMenuOpen.value = false;
    isUserMenuOpen.value = false;
  }
};

// Watchers
watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false;
  }
);

// Lifecycle
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
  window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.nav-item {
  @apply px-4 py-2.5 rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white;
}

.router-link-active {
  @apply bg-primary/5 text-primary font-medium hover:bg-primary/10 hover:text-primary;
}
</style>
